(ns fen.screen
  "Drawing a screen. It knows rows, labels and the popup that lists what can
  be done here - and nothing about entities, fields or the store.

  It looks like a terminal because it reads like one: an outline of lines
  in one typeface, and a colour that says what a thing is rather than
  where it is. A key is green, a reference is cyan, a kind is violet, what
  came with the program is amber (see fen.theme). The lines down the left
  are the tree - a row hangs where its line comes from.

  A row does the one thing it has when it is pressed: a reference folds
  and unfolds, everything else asks what can be done with it. The three
  dots at its right end always ask. On a keyboard that is enter and space,
  the arrows walk and fold (see fen.keys). Nothing here is drawn twice for
  the two pointers: what a finger needs more of is in the stylesheet behind
  `pointer: coarse` (see public/index.html).

  The elements are MUI's (see fen.mui): a row is a list item, the popup and
  the text put up to be read are dialogs, and what they know about focus,
  about the escape key and about a finger landing on them is theirs. What
  they are showing is still only what the server handed over."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [fen.auth :as auth]
            [fen.command :as command]
            [fen.login :as login]
            [fen.mui :as mui :refer [sx]]
            [fen.theme :refer [colors]]
            [fen.window :as window]
            [kc.core :as kc]))

;;; ------------------------------------------------------------- the words

(def-ui Key
  "The key that runs something, drawn as a key: a small boxed letter. Only
  where there is a keyboard - on a phone it is a letter nobody can press."
  [k]
  ($ :kbd {:class "ento-key ento-by-key"} k))

(def-ui Prompt
  "The prompt sign, in green, in front of what the window is about. A
  terminal's way of saying: this is the line being worked on."
  []
  ($ :span {:class "ento-prompt"} ">"))

(def-ui Kind
  "What kind of thing this is, as a small violet tag. The window is handed
  the word and does not know what it means."
  [kind]
  ($ :span {:class "ento-kind"} kind))

(def-ui Id
  "The ID, dim and as the keyword it is, because that is what the file is
  called and what anybody would search for."
  [id]
  ;; Long enough to matter: an id is one word with no space in it, and a
  ;; long one would push the whole page sideways (see Row).
  ($ :span {:class "ento-id"} (str ":" (name id))))

(def origins
  "Where an entity stands between the program and the person: a word and a
  colour for each. The window shows them and does not know what they mean -
  how the two files are laid on top of each other is the server's business
  (see ento.store)."
  {:seed    {:label "program"         :color (:seed colors)}
   :added   {:label "program + yours" :color (:seed colors)}
   :own     {:label "yours"           :color (:key colors)}
   :missing {:label "new"             :color (:warn colors)}})

(def-ui Origin [origin]
  (when-let [{:keys [label color]} (get origins origin)]
    ($ mui/Chip {:label label
                 :sx (sx {:color color :borderColor color :opacity 0.85})})))

;;; -------------------------------------------------------------- the rows

(defn- fold!
  "Open what is closed, close what is open. It asks the server, because the
  rows underneath can only come from there."
  [{:keys [handle folded?]}]
  (window/send! {:type (if folded? :unfold :fold) :handle handle}))

(def-ui Row [row cursor?]
  (let [{:keys [handle depth key label annotation id origin foldable? folded? fixed? actions state]} row
        loading? (= :loading state)]
    ;; The handle is on the element as well as in the data: the keyboard
    ;; finds the row it just moved to by it, and a handle means the same
    ;; thing on both sides (see fen.keys).
    ($ mui/ListItemButton
       {:className (str "ento-row"
                        (when foldable? " ento-row-ref")
                        (when (and foldable? (not folded?)) " ento-row-open")
                        ;; Paler, and nothing else: the row is still read,
                        ;; still opened, still shown as EDN. What it is not
                        ;; is yours to change, and that is a thing to see
                        ;; before reaching for it (see public/index.html).
                        (when fixed? " ento-row-fixed"))
        :data-handle handle
        ;; The gutters are the stylesheet's, so a finger can be given more
        ;; of them (see public/index.html).
        :disableGutters true
        :selected cursor?
        ;; A press is the row's one gesture, and it does the one thing the
        ;; row has: a reference folds, everything else asks. The cursor
        ;; comes along either way, so what the keyboard does next is about
        ;; this row.
        :onClick (fn [_]
                   (window/cursor! handle)
                   (if foldable? (fold! row) (window/open-menu! handle)))
        :sx (sx {:borderBottom (str "1px solid " (:line colors))})}
       ;; The tree, as lines: one for every level this row hangs under,
       ;; drawn as a repeating gradient so a depth of five is one span and
       ;; not five. It stretches the row's whole height, padding included,
       ;; which is why the padding is on the cells and not on the row.
       (when (pos? (or depth 0))
         ($ :span {:class "ento-guides"
                   :style {:width (str (* 1.4 depth) "rem")}}))
       ;; The triangle: drawn by the stylesheet and turned when open, so the
       ;; eye can follow it turning. On a row without children it is empty
       ;; space, so the text of every row at one depth starts in the same
       ;; column.
       ($ :span {:class "ento-fold"}
          (when foldable?
            ($ :span {:class "ento-chevron"
                      :style {:transform (if folded? "rotate(0deg)" "rotate(90deg)")}})))
       ;; Everything the row says, in one cell that wraps inside itself:
       ;; what does not fit goes below rather than off to the side, and the
       ;; tree lines and the dots stay where they are. Sideways is the one
       ;; direction a page may not grow in - a phone cannot follow it.
       ($ :span {:class "ento-cell"}
          ;; The key and what it says are one thing in the row and not two:
          ;; a key is read with its value, and kept apart they are wrapped
          ;; apart - the key alone on a line and the value under it.
          ;;
          ;; This is also the part that may be any length. A value can be
          ;; longer than the window is wide - a URL, an id, a line pasted in
          ;; with nowhere to break - and it wraps, and breaks mid-word when
          ;; the word itself is longer than the line.
          ($ :span {:className (str "ento-value" (when foldable? " ento-ref") (when loading? " ento-loading"))}
             (when key ($ :span {:class "ento-k"} key))
             (if loading? "…" label))
          (when annotation ($ Kind {:kind annotation}))
          (when id ($ Id {:id id}))
          (when origin ($ Origin {:origin origin})))
       ;; Not the actions themselves, only that there are some and the way
       ;; in. A finger sees it on every row - it is the only way in it has
       ;; on a reference, whose press is taken by folding. A mouse sees it
       ;; where it points (see public/index.html).
       (when (seq actions)
         ($ :span {:class "ento-more"
                   :role "button"
                   :aria-label "commands"
                   :onClick (fn [^js e]
                              (.stopPropagation e)
                              (window/cursor! handle)
                              (window/open-menu! handle))}
            "⋯")))))

;;; ------------------------------------------------------------ the popups

(def-ui Command
  "One offer in the popup: the key that runs it, what it is called, and a
  line saying what it really does - which is the whole reason the popup beats
  a row of little words."
  [offer index picked?]
  (let [{:keys [action]} offer]
    ($ mui/ListItemButton
       {:className "ento-offer"
        :disableGutters true
        :selected picked?
        :onClick (fn [_] (command/act! offer))
        :onMouseEnter (fn [_] (window/menu-index! index))}
       ($ :span {:class "ento-offer-key"} ($ Key {:k (:key action)}))
       ($ mui/ListItemText
          {:primary (:label action)
           :secondary (:description action)
           :slotProps (sx {:secondary {:sx {:fontSize ".85em" :marginTop ".1rem"}}})}))))

(def-ui Commands
  "What can be done here, as a list to read rather than a line of words to
  guess at. Space opens it on the row the cursor is on, escape closes it, and
  it holds the keyboard in between (see fen.keys).

  The dialog closes itself on escape and on a tap outside, which is the same
  thing the keyboard does with it - said twice on purpose, because the two
  ways in are the mouse's and the finger's and neither may depend on the
  other being there. On a phone it comes up from the bottom, where the
  thumb already is (see public/index.html)."
  [menu screen]
  (let [offers (command/offered screen menu)
        picked (:index menu)]
    ($ mui/Dialog {:open true
                   :className "ento-overlay ento-sheet"
                   :maxWidth "sm"
                   :onClose (fn [_ _] (window/close-menu!))}
       ($ mui/List {:disablePadding true}
          (if (seq offers)
            ;; The subheaders are children of the list and not wrappers
            ;; around what they name: a group is a line above its offers,
            ;; not a box around them.
            (mapcat (fn [[i o]]
                      (cond-> []
                        (or (zero? i) (not= (:group o) (:group (nth offers (dec i)))))
                        (conj ($ mui/ListSubheader {:key (str "group/" i)} (:group o)))

                        :always
                        (conj ($ Command {:key (str i) :offer o
                                          :index i :picked? (= i picked)}))))
                    (map-indexed vector offers))
            ($ mui/Box {:sx (sx {:padding ".5rem .75rem" :color "text.secondary"})}
               "Nothing to do here."))))))

(def-ui Ask
  "An action asking for its input, in a box of the window's own: the
  question, a field when it wants text, and the two ways out.

  What it wants is read from the action and nothing else - a `:confirm`
  gets a question and a yes, anything else gets a field with what is there
  now already in it. Enter sends, escape cancels, and shift+enter is a new
  line, because a value may have several (see fen.command)."
  [offer]
  (let [{:keys [action]} offer
        {:keys [label description input]} action
        confirm? (= :confirm (:type input))
        [text set-text] (kc/use-state (or (:value input) ""))]
    ($ mui/Dialog {:open true
                   :className "ento-overlay ento-sheet"
                   :maxWidth "sm"
                   :onClose (fn [_ _] (command/cancel!))}
       ($ mui/DialogTitle ($ Prompt) ($ :span label))
       ($ mui/DialogContent
          ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".85em"
                                      :marginBottom (if confirm? 0 ".75rem")})}
             (if confirm? (or (:question input) label) description))
          (when-not confirm?
            ($ mui/TextField
               {:value text
                :autoFocus true
                :fullWidth true
                :multiline true
                :maxRows 8
                :size "small"
                :onChange (fn [^js e] (set-text (-> e .-target .-value)))
                :onKeyDown (fn [^js e]
                             (when (and (= "Enter" (.-key e)) (not (.-shiftKey e)))
                               (.preventDefault e)
                               (command/answer! offer text)))})))
       ($ mui/DialogActions
          ($ mui/Button {:onClick (fn [_] (command/cancel!))
                         :sx (sx {:color "text.secondary"})}
             "cancel")
          ($ mui/Button {:variant "outlined"
                         :color (if confirm? "warning" "primary")
                         :autoFocus confirm?
                         :onClick (fn [_] (command/answer! offer (when-not confirm? text)))}
             (if confirm? "yes" "ok"))))))

(def-ui Shown
  "Something to read, over everything else: a title, the text as it came,
  and a way back out. Not a screen of its own - what is being read is about
  the entity underneath, and closing it leaves that entity where it was.

  The window is handed text and a title and shows them. It is not told what
  the text is, which is how an entity can be shown as EDN without this file
  learning what an entity is (see ento.presenter).

  It is the answer to one question, asked once: what is up here does not
  follow the entity while it is being read, and the outline behind it does."
  [shown]
  ($ mui/Dialog {:open true
                 :className "ento-overlay ento-sheet"
                 :maxWidth "md"
                 :scroll "paper"
                 :onClose (fn [_ _] (window/close-show!))}
     ($ mui/DialogTitle
        ($ Prompt)
        ($ :span (:title shown))
        ($ :span {:style {:flex "1"}})
        ;; The way out, said in the words of the pointer at hand: a finger
        ;; has no escape key, and a keyboard is quicker than aiming at a
        ;; button.
        ($ mui/Button {:className "ento-screen-action"
                       :onClick (fn [_] (window/close-show!))
                       :sx (sx {:color "text.secondary" :fontWeight 400})}
           ($ :span {:class "ento-by-key"} "esc")
           ($ :span {:class "ento-by-tap"} "close")))
     ($ mui/DialogContent
        ;; As it came. Long lines wrap rather than run off the side -
        ;; sideways is where a phone cannot follow them.
        ($ mui/Box {:component "pre"
                    :sx (sx {:margin 0 :whiteSpace "pre-wrap"
                             :overflowWrap "anywhere" :font "inherit"
                             :fontSize ".9em" :lineHeight 1.5})}
           (:text shown)))))

;;; ------------------------------------------------------------ the screen

(def-ui Header
  "The line at the top: what the window is looking at, as a prompt. It
  stays at the top while the outline scrolls under it, so a long entity
  never loses its name."
  [screen]
  ($ mui/Box {:className "ento-header"}
     ($ :span {:class "ento-stripe"})
     ($ mui/Box {:sx (sx {:display "flex" :alignItems "baseline" :gap ".6rem"
                          :flexWrap "wrap" :padding ".6rem .75rem"
                          ;; The header wraps, so a part that does not fit
                          ;; goes to the next line by itself. Only one that
                          ;; is wider than the whole line is left, and that
                          ;; one breaks rather than pushing the page
                          ;; sideways (see Row).
                          "& > *" {:minWidth 0 :overflowWrap "anywhere"}})}
        ($ Prompt)
        ($ :span {:class "ento-title"} (or (:title screen) "ento"))
        ($ :span {:class "ento-caret"})
        (when-let [k (:kind screen)] ($ Kind {:kind (name k)}))
        (when-let [i (:id screen)] ($ Id {:id i}))
        ;; Always here, unlike on a row: what the whole window is looking
        ;; at is worth one word even when the word is "yours".
        ($ Origin {:origin (:origin screen)}))))

(def-ui Dock
  "The bar at the bottom, where a thumb already is: whether the server is
  there, how the keyboard gets at things, and what can be done to the
  entity as a whole. The entity's own actions live here and not in the
  header because on a phone the top of the screen is the far end of it."
  [screen link]
  ($ mui/Box {:className "ento-dock"}
     ($ :span {:className (str "ento-link ento-link-" (name (or link :waiting)))}
        ($ :span {:class "ento-dot"})
        (case link :up "live" :down "offline" "…"))
     ($ :span {:class "ento-by-key ento-dock-hint"}
        "↑↓ walk · ←→ fold · space commands")
     ($ :span {:style {:flex "1"}})
     (for [a (:actions screen)]
       ($ mui/Button {:key (str "screen/" (:id a))
                      :className "ento-screen-action"
                      :title (:description a)
                      :onClick (fn [_] (command/act! {:handle nil :action a}))
                      :sx (sx {:color "text.primary" :gap ".4rem"})}
          ($ Key {:k (:key a)})
          (:label a)))))

(def-ui Screen []
  (let [{:keys [screen link view menu shown ask]} (kc/use-atom window/WINDOW)
        {:keys [needed?]} (kc/use-atom auth/AUTH)]
    (if needed?
      ;; Nothing of the graph while the server is refusing to hand it out -
      ;; there is no older screen the window would be allowed to keep showing.
      ($ login/Login)
      ($ mui/Box {:className "ento-page"}
         ($ Header {:screen screen})
         ($ mui/List {:disablePadding true}
            (for [row (:rows screen)]
              ($ Row {:key (:handle row) :row row
                      :cursor? (= (:cursor view) (:handle row))})))
         ;; What the screen itself has to say - an entity nothing has been
         ;; said about yet would otherwise just be an empty page.
         (when-let [s (:status screen)]
           ($ mui/Box {:sx (sx {:padding ".75rem" :color "text.secondary" :fontSize ".9em"})}
              "# " s))
         ($ Dock {:screen screen :link link})
         (when menu ($ Commands {:menu menu :screen screen}))
         (when ask ($ Ask {:offer ask}))
         (when shown ($ Shown {:shown shown}))))))
